import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Mutation,
  MutationSetVisitorByIdArgs,
  Query,
  QueryGetVisitorByIdArgs,
  VisitorGroupMembershipInput
} from 'graphql/generated/graphqlTypes';
import { SET_VISITOR_BY_ID_WITH_ROLE } from 'graphql/v2/mutations';
import {
  GET_ALL_VISITORS_FOR_TENANT,
  GET_ALL_VISITORS_VISITOR_GROUPS,
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS,
  GET_VISITOR_DETAILS,
  GET_VISITOR_DETAILS_VISITOR_GROUPS,
  GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import { preserveExistingMembershipDetails, updateVisitorGroupMembershipWithAdminGroup } from './utils';

interface Props extends BaseHookProps {
  visitorId: string;
}

const useEditVisitorGroups = ({ visitorId, handleFetchError }: Props) => {
  const { translation } = useTranslation();
  const {
    loading: coContractorsVisitorGroupsLoading,
    error: coContractorsVisitorGroupsError,
    data: coContractorsVisitorGroupsData
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_ALL_VISITORS_VISITOR_GROUPS, {
    variables: {
      filter: { type: VisitorGroupType.CoContractors }
    }
  });

  useEffect(() => {
    if (coContractorsVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(coContractorsVisitorGroupsError);
    }
  }, [coContractorsVisitorGroupsError]);

  const {
    loading: visitorDetailsLoading,
    error: visitorDetailsError,
    data: visitorDetails
  } = useQuery<Pick<Query, 'getVisitorById'>, QueryGetVisitorByIdArgs>(GET_VISITOR_DETAILS, {
    variables: { id: visitorId! }
  });

  useEffect(() => {
    if (visitorDetailsError) {
      handleFetchError(translation.user_details_error_fetching);
      console.error(visitorDetailsError);
    }
  }, [visitorDetailsError]);

  const [fetchVisitorDetails, { loading: fetchVisitorDetailsLoading, error: fetchVisitorDetailsError }] = useLazyQuery<
    Pick<Query, 'getVisitorById'>,
    QueryGetVisitorByIdArgs
  >(GET_VISITOR_DETAILS, {
    variables: { id: visitorId! }
  });

  useEffect(() => {
    if (fetchVisitorDetailsError) {
      handleFetchError(translation.user_details_error_fetching);
      console.error(fetchVisitorDetailsError);
    }
  }, [fetchVisitorDetailsError]);

  const [updateVisitorMutation, { loading: updateVisitorLoading }] = useMutation<
    Pick<Mutation, 'setVisitorById'>,
    MutationSetVisitorByIdArgs
  >(SET_VISITOR_BY_ID_WITH_ROLE, {
    refetchQueries: [
      { query: GET_ALL_VISITORS_FOR_TENANT },
      { query: GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS },
      { query: GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS },
      { query: GET_VISITOR_DETAILS, variables: { id: visitorId } },
      { query: GET_VISITOR_DETAILS_VISITOR_GROUPS }
    ],
    awaitRefetchQueries: true,
    onError: (error) => {
      if (error.message === 'Visitor already exists') handleFetchError(translation.user_email_already_exists);
      else if (error.message === 'Visitor is not only CO_CONTRACTOR')
        handleFetchError(translation.expiration_date_error_installer);
      else handleFetchError(translation.external_groups_user_error_adding);
    }
  });

  const [
    fetchAdministratorsVisitorGroups,
    {
      loading: administratorsVisitorGroupsLoading,
      data: administratorsVisitorGroupsData,
      error: administratorsVisitorGroupsError
    }
  ] = useLazyQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_ALL_VISITORS_VISITOR_GROUPS, {
    variables: {
      filter: { type: VisitorGroupType.Administrators }
    }
  });

  useEffect(() => {
    if (administratorsVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(administratorsVisitorGroupsError);
    }
  }, [administratorsVisitorGroupsError]);

  useEffect(() => {
    fetchAdministratorsVisitorGroups();
  }, [fetchAdministratorsVisitorGroups]);

  const [
    fetchInstallersVisitorGroups,
    { loading: installersVisitorGroupsLoading, data: installersVisitorGroupsData, error: installersVisitorGroupsError }
  ] = useLazyQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_ALL_VISITORS_VISITOR_GROUPS, {
    variables: {
      filter: { type: VisitorGroupType.Installers }
    }
  });

  useEffect(() => {
    if (installersVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(installersVisitorGroupsError);
    }
  }, [installersVisitorGroupsError]);

  useEffect(() => {
    fetchInstallersVisitorGroups();
  }, [fetchInstallersVisitorGroups]);

  const updateVisitorGroupsMembership = async (
    visitorGroupMembership: VisitorGroupMembershipInput[],
    shouldBeAdmin: boolean,
    shouldBeInstaller: boolean
  ) => {
    {
      let result;
      const [visitorDetailsData, adminGroupData, installerGroupData] = await Promise.all([
        fetchVisitorDetails(),
        fetchAdministratorsVisitorGroups(),
        fetchInstallersVisitorGroups()
      ]);
      if (
        visitorDetailsData.data?.getVisitorById === undefined ||
        adminGroupData.data?.getVisitorGroupsForTenant === undefined ||
        installerGroupData.data?.getVisitorGroupsForTenant === undefined
      ) {
        handleFetchError(translation.user_groups_error_updating);
      }
      if (visitorDetailsData.data?.getVisitorById) {
        const {
          name,
          id,
          visitorGroupMembership: visitorDetailsGroupMembership
        } = visitorDetailsData.data?.getVisitorById;

        const entraMembership = visitorDetailsGroupMembership.find(
          (group) => group.visitorGroup.type === VisitorGroupType.AzureAd
        );
        const isEntraAlreadyIncluded = visitorGroupMembership.some(
          (membership) => membership.visitorGroupId === entraMembership?.visitorGroup.id
        );
        const membershipWithEntra =
          entraMembership && !isEntraAlreadyIncluded
            ? [
                ...visitorGroupMembership,
                { visitorGroupId: entraMembership.visitorGroup.id, role: entraMembership.role }
              ]
            : visitorGroupMembership;

        if (adminGroupData.data?.getVisitorGroupsForTenant && installerGroupData.data?.getVisitorGroupsForTenant) {
          const updatedVisitorGroupMembership: VisitorGroupMembershipInput[] =
            updateVisitorGroupMembershipWithAdminGroup({
              visitorGroupMembership: membershipWithEntra,
              options: {
                shouldBeAdmin,
                adminGroup: adminGroupData.data?.getVisitorGroupsForTenant.find(
                  (group) => group.type === VisitorGroupType.Administrators
                ),
                shouldBeInstaller,
                installerGroup: installerGroupData.data?.getVisitorGroupsForTenant.find(
                  (group) => group.type === VisitorGroupType.Installers
                )!
              }
            });

          result = await updateVisitorMutation({
            variables: {
              name,
              visitorId: id,
              visitorGroupMembership: preserveExistingMembershipDetails(
                updatedVisitorGroupMembership,
                visitorDetailsGroupMembership
              )
            }
          });
        }
      }
      return result;
    }
  };

  return {
    coContractors: coContractorsVisitorGroupsData?.getVisitorGroupsForTenant || [],
    coContractorsLoading: coContractorsVisitorGroupsLoading,
    visitorDetails: visitorDetails?.getVisitorById,
    visitorDetailsLoading,
    administratorsVisitorGroups: administratorsVisitorGroupsData?.getVisitorGroupsForTenant,
    administratorsVisitorGroupsLoading,
    installersVisitorGroups: installersVisitorGroupsData?.getVisitorGroupsForTenant,
    installersVisitorGroupsLoading,
    updateVisitorGroupsMembership,
    updateVisitorGroupsMembershipLoading:
      updateVisitorLoading ||
      visitorDetailsLoading ||
      fetchVisitorDetailsLoading ||
      administratorsVisitorGroupsLoading ||
      installersVisitorGroupsLoading
  };
};

export default useEditVisitorGroups;
